import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { WidgetError } from "@/components/WidgetError";
import { PaginationBar } from "@/components/PaginationBar";
import { Download, FileText } from "lucide-react";

/**
 * Customer Portal — Invoices list with PDF download.
 * All requests are scoped by JWT (backend hard-scopes to the logged-in
 * customer_account_id). No customer_account_id is sent from client.
 */
export default function PortalInvoices() {
  const [d, setD] = useState({ items: [], pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get("/portal/finance/invoices", { params: { page, limit: 20 } })
      .then(({ data }) => setD(data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [page]);
  useEffect(load, [load]);

  const download = async (iid, invNo) => {
    try {
      const res = await api.get(`/portal/finance/invoices/${iid}/pdf`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${invNo || iid}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { /* handled by toast at api layer */ }
  };

  if (error) {
    return <WidgetError error={error} onRetry={load}
                        title="Could not load invoices" testId="portal-invoices-error"/>;
  }

  return (
    <div data-testid="portal-invoices-page">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={20}/>
        <h1 className="text-3xl font-black tracking-tight">Invoices</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Invoice #</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Due</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading…</td></tr>
            ) : d.items.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-xs text-slate-500">No invoices yet.</td></tr>
            ) : d.items.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100"
                  data-testid={`portal-invoice-row-${inv.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{(inv.issued_at || inv.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs">{inv.due_date || "—"}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] font-bold uppercase">{inv.status}</span>
                </td>
                <td className="px-3 py-2 font-mono text-right">{fmtMoney(inv.total_cents)}</td>
                <td className="px-3 py-2">
                  {inv.status !== "draft" && (
                    <button onClick={() => download(inv.id, inv.invoice_no)}
                            data-testid={`portal-invoice-download-${inv.id}`}
                            className="text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center gap-1">
                      <Download size={12}/> PDF
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={d.pages || 1} onChange={setPage}/>
    </div>
  );
}
